import { useEffect, useMemo } from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';

/**
 * LegSplitInput
 * --------------
 * Allocates a PO's `taxable_value` across four legs:
 *   Mens · Womens (additional) · Ground 2 (Daly) · Common
 *
 * Props:
 *   value         -> { mens, womens, ground2, common } | null
 *   onChange      -> (newValue) => void
 *   taxableValue  -> number — the figure the four legs must sum to
 *   suggestion    -> { mens, womens, ground2, common } | null  optional auto-suggest
 *   onUseSuggestion -> () => void  fired when user clicks "Use suggestion"
 *   disabled      -> bool
 *
 * The component never mutates props directly — it always emits a new object.
 * Validation (sum == taxableValue ±1) is enforced client-side here as a quick
 * UX hint and again on the backend on save.
 */
const LEGS = [
  { key: 'mens',    label: 'Mens',           color: 'bg-blue-50 border-blue-200 text-blue-700' },
  { key: 'womens',  label: 'Womens',         color: 'bg-pink-50 border-pink-200 text-pink-700' },
  { key: 'ground2', label: 'Ground 2',       color: 'bg-amber-50 border-amber-200 text-amber-700' },
  { key: 'common',  label: 'Common',         color: 'bg-slate-50 border-slate-200 text-slate-700' },
];

const blank = () => ({ mens: 0, womens: 0, ground2: 0, common: 0 });

export const LegSplitInput = ({ value, onChange, taxableValue, suggestion, onUseSuggestion, disabled = false, disableAutoDefault = false }) => {
  const safe = value || blank();
  const tv = Number(taxableValue) || 0;

  const sum = useMemo(
    () => Number(safe.mens || 0) + Number(safe.womens || 0) + Number(safe.ground2 || 0) + Number(safe.common || 0),
    [safe]
  );
  const remaining = Number((tv - sum).toFixed(2));
  const ok = Math.abs(remaining) < 1;

  // Auto-default to {common: taxableValue} when value is empty (or all zeros)
  // and taxableValue is set. Once the user has any non-zero allocation we
  // never overwrite — even if they later edit taxable_value.
  // Skip entirely when the parent passes disableAutoDefault — useful when the
  // parent computes a budget-head-derived split itself.
  useEffect(() => {
    if (disableAutoDefault) return;
    if ((!value || sum === 0) && tv > 0 && !disabled) {
      onChange({ ...blank(), common: tv });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tv]);

  const update = (leg, raw) => {
    const n = raw === '' ? 0 : Number(raw);
    onChange({ ...(value || blank()), [leg]: Number.isFinite(n) && n >= 0 ? n : 0 });
  };

  return (
    <div className="space-y-2 border rounded-md p-3 bg-muted/10">
      <div className="flex items-center justify-between">
        <Label className="text-xs uppercase tracking-wider text-muted-foreground">
          Leg allocation (Mens / Womens / Ground 2 / Common)
        </Label>
        {suggestion && onUseSuggestion && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 text-xs"
            onClick={onUseSuggestion}
            disabled={disabled}
            data-testid="leg-split-use-suggestion"
          >
            Use suggested split
          </Button>
        )}
      </div>
      <div className="grid grid-cols-4 gap-2">
        {LEGS.map((l) => (
          <div key={l.key} className={`rounded border ${l.color} p-2`}>
            <div className="text-[10px] uppercase tracking-wider mb-1">{l.label}</div>
            <Input
              type="number"
              min="0"
              step="0.01"
              value={safe[l.key] === 0 ? '' : safe[l.key]}
              onChange={(e) => update(l.key, e.target.value)}
              disabled={disabled}
              placeholder="0"
              className="h-8 text-sm"
              data-testid={`leg-split-${l.key}`}
            />
          </div>
        ))}
      </div>
      <div className={`text-[11px] flex items-center justify-between ${ok ? 'text-emerald-700' : 'text-destructive'}`}>
        <span>
          Sum: ₹{sum.toLocaleString('en-IN')} · Target: ₹{tv.toLocaleString('en-IN')}
        </span>
        <span className="font-medium">
          {ok ? '✓ matches taxable value' : `${remaining > 0 ? 'Short by' : 'Over by'} ₹${Math.abs(remaining).toLocaleString('en-IN')}`}
        </span>
      </div>
    </div>
  );
};

/** Compact display badges used in PO tables. Renders only legs > 0. */
export const LegBadges = ({ legSplit }) => {
  if (!legSplit) {
    return (
      <span
        className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] border border-slate-200 bg-slate-50 text-slate-600"
        data-testid="leg-badges-untagged"
      >
        Untagged
      </span>
    );
  }
  const items = LEGS.filter((l) => Number(legSplit[l.key] || 0) > 0);
  if (items.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1">
      {items.map((l) => (
        <span
          key={l.key}
          className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] border ${l.color}`}
          title={`${l.label}: ₹${Number(legSplit[l.key]).toLocaleString('en-IN')}`}
        >
          {l.label[0]}
          {l.key === 'ground2' ? '2' : ''}
          <span className="ml-1 font-mono">
            ₹{Number(legSplit[l.key]).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
          </span>
        </span>
      ))}
    </div>
  );
};

export default LegSplitInput;
